/* eslint-disable max-lines */
import { SEPARATOR } from '@hakimo-ui/hakimo/data-access';
import { UnifiedAlarm, MultiClickSeekState } from '@hakimo-ui/hakimo/types';
import dayjs from 'dayjs';
import dayjsTimezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { RefObject } from 'react';
import {
  MULTI_CLICK_WINDOW_MS,
  SCAN_GAP_SKIP_MS,
  SEEK_STEP_DOUBLE_MS,
  SEEK_STEP_MS,
  SEEK_STEP_TRIPLE_MS,
} from '../constants';
dayjs.extend(utc);
dayjs.extend(dayjsTimezone);

/**
 * SCAN timelines: an alarm's last frame stays on screen until the next item's
 * first frame — the clock plays through gaps <= SCAN_GAP_SKIP_MS (holding that
 * frame) and skips larger ones. Bars end at the last frame's timestamp, so those
 * held sub-threshold gaps render empty. Given items pre-sorted by start, return the
 * rendered end for each: extended to the next item's start when the gap is small,
 * else its own end. Genuine (skipped) gaps stay visible. Used by both the per-camera
 * and compressed timelines so the rule can't drift between them.
 */
export function bridgeGaps<T>(
  items: T[],
  getStart: (t: T) => number,
  getEnd: (t: T) => number
): number[] {
  return items.map((item, i) => {
    const end = getEnd(item);
    const next = items[i + 1];
    if (next) {
      const nextStart = getStart(next);
      if (nextStart > end && nextStart - end <= SCAN_GAP_SKIP_MS) {
        return nextStart;
      }
    }
    return end;
  });
}

export const formatTime = (
  date: Date,
  timeZone: string,
  showTimezoneName?: boolean
) => {
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: timeZone,
    timeZoneName: showTimezoneName ? 'short' : undefined,
  });
};

export const getHoverTranslate = (
  position: number,
  timelineRef: RefObject<HTMLDivElement>
) => {
  if (!timelineRef.current || position === null) return '50%';

  const timelineWidth = timelineRef.current.offsetWidth;
  const imageWidth = timelineWidth / 2; // Assuming image width is half of timeline width
  const hoverX = (position / 100) * timelineWidth;

  if (hoverX < imageWidth / 2) {
    return '0%'; // Align to left edge
  } else if (hoverX > timelineWidth - imageWidth / 2) {
    return '100%'; // Align to right edge
  } else {
    return '50%'; // Center
  }
};

export function getTruncatedText(
  text: string,
  containerRef: RefObject<HTMLElement>
): string {
  if (!containerRef.current) return text;
  // Get the width of the container
  const containerWidth = containerRef.current.offsetWidth;
  // Approximate width of a single character in pixels
  const charWidth = 7;
  // Calculate how many characters can fit in the container
  const maxChars = Math.floor(containerWidth / charWidth);
  if (text.length <= maxChars) {
    // No truncation needed
    return text;
  }
  // Calculate how many characters to show at the start and end
  const halfChars = Math.floor(maxChars / 2);

  return `${text.slice(0, halfChars)}...${text.slice(-halfChars)}`;
}

export function formatDateInTimezone(
  timestampInSeconds: number,
  timezone: string,
  showDate = true
) {
  const format = showDate ? 'DD-MMM-YY, HH:mm:ss' : 'HH:mm:ss';
  return dayjs.unix(timestampInSeconds).tz(timezone).format(format);
}

// Determine the dominant alarm based on priority rules
export const getDominantAlarm = (alarms: UnifiedAlarm[]): UnifiedAlarm => {
  // Priority order:
  // 1. High severity alarms
  // 2. Video alarms over frame alarms
  // 3. Unresolved over resolved
  // 4. Most recent alarm

  const sortedAlarms = [...alarms].sort((a, b) => {
    // High severity first
    if (a.severity === 'high' && b.severity !== 'high') return -1;
    if (b.severity === 'high' && a.severity !== 'high') return 1;

    // Video over frames
    if (a.media.type === 'video' && b.media.type === 'frames') return -1;
    if (b.media.type === 'video' && a.media.type === 'frames') return 1;

    // Unresolved over resolved
    if (!a.resolvedAlarm && b.resolvedAlarm) return -1;
    if (a.resolvedAlarm && !b.resolvedAlarm) return 1;

    // Most recent
    return b.alarmStartTime - a.alarmStartTime;
  });

  return sortedAlarms[0];
};

export function createMultiClickSeekState(): MultiClickSeekState {
  return { direction: 1, count: 0, appliedMs: 0, timer: null };
}

export function getSeekStepForClickCount(count: number): number {
  if (count <= 1) return SEEK_STEP_MS;
  if (count === 2) return SEEK_STEP_DOUBLE_MS;
  return (count - 2) * SEEK_STEP_TRIPLE_MS;
}

export function registerMultiClickSeek(
  state: MultiClickSeekState,
  direction: 1 | -1,
  onSeek: (deltaMs: number) => void,
  onReset?: () => void
) {
  const sameDir = state.direction === direction && state.timer !== null;
  if (!sameDir) {
    if (state.timer) clearTimeout(state.timer);
    state.count = 0;
    state.appliedMs = 0;
  }
  state.direction = direction;
  state.count += 1;
  const target = getSeekStepForClickCount(state.count);
  const delta = target - state.appliedMs;
  state.appliedMs = target;
  onSeek(direction * delta);
  if (state.timer) clearTimeout(state.timer);
  state.timer = setTimeout(() => {
    state.count = 0;
    state.appliedMs = 0;
    state.timer = null;
    onReset?.();
  }, MULTI_CLICK_WINDOW_MS);
}

export function getNeighborAlarmsSearchParams(
  startTimestamp: number,
  endTimestamp: number,
  sourceEntityIds: string[],
  locationId?: number,
  status?: string,
  operatorId?: string,
  product?: string
): string {
  const params = new URLSearchParams();
  params.append('startTimestamp', String(startTimestamp));
  params.append('endTimestamp', String(endTimestamp));
  params.append('sourceEntityIds', sourceEntityIds.join(SEPARATOR));
  locationId && params.append('locationId', String(locationId));
  status && params.append('statuses', status);
  operatorId && params.append('operatorId', operatorId);
  product && params.append('product', product);
  return params.toString();
}
